import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGovernanceContract, getGovernanceSignerContract, Proposal, ProposalState, VoteType } from '../contracts/contract';
import { useNetwork } from '../contexts/NetworkContext';
import { useWallet } from '../contexts/WalletContext';
import { useGovernance } from '../contexts/GovernanceContext';
import Header from './Header';
import ProposalList from './ProposalList';
import CreateProposalForm from './CreateProposalForm';
import VotingPanel from './VotingPanel';
import GovernanceHeader from './GovernanceHeader';
import PolicyDescription from './PolicyDescription';
import './GovernancePage.css';

// Fetch a single chunk with retry logic
const fetchChunkWithRetry = async (contract: any, fromBlock: number, toBlock: number, chunkNumber: number): Promise<any[]> => {
  const maxRetries = 3;
  
  for (let retries = maxRetries; retries > 0; retries--) {
    try {
      console.log(`Fetching chunk ${chunkNumber}: blocks ${fromBlock} to ${toBlock} (retries left: ${retries})`);
      const filter = contract.filters.ProposalCreated();
      const chunkEvents = await contract.queryFilter(filter, fromBlock, toBlock);
      return chunkEvents;
      
    } catch (chunkError: any) {
      if (chunkError.message?.includes('circuit breaker') || chunkError.message?.includes('Execution prevented')) {
        console.warn(`Circuit breaker open for chunk ${chunkNumber}, waiting before retry...`);
        // Wait longer for circuit breaker to reset
        await new Promise(resolve => setTimeout(resolve, 2000 * (maxRetries - retries + 1)));
      } else {
        console.warn(`Chunk ${chunkNumber} failed:`, chunkError);
        return []; // Non-circuit breaker error, return empty array
      }
    }
  }
  
  return []; // All retries exhausted
};

// Cache for deployment blocks to avoid re-fetching
const deploymentBlockCache = new Map<string, number>();

// Get the deployment block of the contract
const getContractDeploymentBlock = async (contract: any, provider: any, contractAddress: string): Promise<number> => {
  const address = contractAddress.toLowerCase();
  
  // Check cache first
  if (deploymentBlockCache.has(address)) {
    const cachedBlock = deploymentBlockCache.get(address)!;
    console.log(`Using cached deployment block: ${cachedBlock}`);
    return cachedBlock;
  }
  
  try {
    const currentBlock = await provider.getBlockNumber();
    
    // First, check if contract exists at current block
    const currentCode = await provider.getCode(contractAddress, currentBlock);
    if (!currentCode || currentCode === '0x') {
      throw new Error('Contract does not exist at current block');
    }
    
    // Binary search to find the deployment block
    let left = 0;
    let right = currentBlock;
    let deploymentBlock = currentBlock;
    
    // Limit binary search to avoid too many RPC calls
    const maxIterations = 20;
    let iterations = 0;
    
    while (left <= right && iterations < maxIterations) {
      iterations++;
      const mid = Math.floor((left + right) / 2);
      
      try {
        const code = await provider.getCode(contractAddress, mid);
        if (code && code !== '0x') {
          // Contract exists at this block
          deploymentBlock = mid;
          right = mid - 1; // Look for earlier deployment
        } else {
          // Contract doesn't exist at this block
          left = mid + 1;
        }
      } catch (error) {
        // If we can't check this block, assume it's not deployed yet
        left = mid + 1;
      }
    }
    
    console.log(`Contract deployed at block: ${deploymentBlock} (found in ${iterations} iterations)`);
    
    // Cache the result
    deploymentBlockCache.set(address, deploymentBlock);
    
    return deploymentBlock;
  } catch (error) {
    console.warn('Could not determine deployment block, using fallback:', error);
    // Fallback: assume contract was deployed recently (last 100k blocks)
    const currentBlock = await provider.getBlockNumber();
    const fallbackBlock = Math.max(0, currentBlock - 100000);
    
    // Cache the fallback result too
    deploymentBlockCache.set(address, fallbackBlock);
    
    return fallbackBlock;
  }
};

// Fetch events in chunks to avoid circuit breaker
const fetchEventsInChunks = async (contract: any, currentBlock: number, provider: any, contractAddress: string, expectedProposalCount: number, fromBlock?: number): Promise<any[]> => {
  const allEvents: any[] = [];
  const chunkSize = 2000; // Smaller chunks to avoid circuit breaker
  
  // Use provided fromBlock or get the deployment block
  let startBlock: number;
  if (fromBlock !== undefined) {
    startBlock = fromBlock;
    console.log(`Fetching events from provided block ${fromBlock} to current block ${currentBlock}`);
  } else {
    startBlock = await getContractDeploymentBlock(contract, provider, contractAddress);
    console.log(`Fetching events from deployment block ${startBlock} to current block ${currentBlock}`);
  }
  
  console.log(`Using start block: ${startBlock}, chunk size: ${chunkSize}, expecting ${expectedProposalCount} proposals`);
  
  let i = 0;
  while (true) {
    const fromBlock = startBlock + (i * chunkSize);
    const toBlock = Math.min(currentBlock, startBlock + ((i + 1) * chunkSize) - 1);
    
    if (fromBlock > currentBlock) break;
    
    const chunkEvents = await fetchChunkWithRetry(contract, fromBlock, toBlock, i + 1);
    
    if (chunkEvents.length > 0) {
      console.log(`Found ${chunkEvents.length} events in chunk ${i + 1} (blocks ${fromBlock}-${toBlock})`);
      allEvents.push(...chunkEvents);
      
      // Stop if we've found events for all expected proposals
      if (allEvents.length >= expectedProposalCount) {
        console.log(`Found all ${expectedProposalCount} expected proposals, stopping event fetch`);
        break;
      }
    }
    
    // Add a delay between chunks to avoid overwhelming the RPC
    await new Promise(resolve => setTimeout(resolve, 500));
    i++;
  }
  
  console.log(`Total events found across all chunks: ${allEvents.length}`);
  return allEvents;
};

const GovernancePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentNetwork } = useNetwork();
  const { provider, signer, isConnected, onAccountChange } = useWallet();
  const { isAddressSet, contractAddress } = useGovernance();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [eventDataAvailable, setEventDataAvailable] = useState(true);
  const [isRecoveryRefresh, setIsRecoveryRefresh] = useState(false);
  const [governanceStats, setGovernanceStats] = useState({
    totalProposals: 0,
    activeProposals: 0,
    totalVotes: 0,
    quorum: 0
  });

  // Mutex to prevent concurrent loadGovernanceData calls
  const hasLoadedData = useRef(false);
  const isLoadingDataRef = useRef(false);

  // Listen for account changes and redirect to homepage
  useEffect(() => {
    const unsubscribe = onAccountChange((newAccount) => {
      navigate('/');
    });
    
    return unsubscribe;
  }, [onAccountChange, navigate]);

  // Navigate to homepage if not connected or no contract address
  useEffect(() => {
    if (!isConnected || !isAddressSet) {
      navigate('/');
      return;
    }
  }, [isConnected, isAddressSet, navigate]);

  // Load proposals and governance stats
  // Cache management functions
  const getCacheKey = (contractAddress: string) => `governance_events_${contractAddress}`;
  const getRefreshFlagKey = (contractAddress: string) => `governance_refresh_flag_${contractAddress}`;
  
  const loadCachedEvents = useCallback((contractAddress: string) => {
    try {
      const cached = localStorage.getItem(getCacheKey(contractAddress));
      if (cached) {
        const data = JSON.parse(cached);
        return {
          events: data.events || [],
          lastBlock: data.lastBlock || 0,
          timestamp: data.timestamp || 0,
          cacheExists: true
        };
      }
    } catch (err) {
      console.warn('Failed to load cached events:', err);
    }
    return { events: [], lastBlock: 0, timestamp: 0, cacheExists: false };
  }, []);

  const setRefreshFlag = useCallback((contractAddress: string, isRefreshing: boolean) => {
    try {
      if (isRefreshing) {
        localStorage.setItem(getRefreshFlagKey(contractAddress), JSON.stringify({
          isRefreshing: true,
          timestamp: Date.now()
        }));
      } else {
        localStorage.removeItem(getRefreshFlagKey(contractAddress));
      }
    } catch (err) {
      console.warn('Failed to set refresh flag:', err);
    }
  }, []);

  const checkRefreshFlag = useCallback((contractAddress: string) => {
    try {
      const flag = localStorage.getItem(getRefreshFlagKey(contractAddress));
      if (flag) {
        const data = JSON.parse(flag);
        // Consider refresh interrupted if flag is older than 5 minutes
        const isStale = Date.now() - data.timestamp > 5 * 60 * 1000;
        return { isRefreshing: data.isRefreshing && !isStale, isStale };
      }
    } catch (err) {
      console.warn('Failed to check refresh flag:', err);
    }
    return { isRefreshing: false, isStale: false };
  }, []);

  const saveCachedEvents = useCallback((contractAddress: string, events: any[], lastBlock: number) => {
    try {
      // Events are already in serialized format when this function is called
      const cacheData = {
        events,
        lastBlock,
        timestamp: Date.now()
      };
      localStorage.setItem(getCacheKey(contractAddress), JSON.stringify(cacheData));
    } catch (err) {
      console.warn('Failed to save cached events:', err);
    }
  }, []);


  const loadGovernanceData = useCallback(async (forceRefresh: boolean = false) => {
    if (!provider || !contractAddress) return;
    
    // Prevent concurrent calls using ref for immediate checking
    if (isLoadingDataRef.current) {
      console.log('loadGovernanceData already in progress, skipping concurrent call');
      return;
    }

    try {
      isLoadingDataRef.current = true;
      setLoading(true);
      setError('');

      // Check for interrupted refresh and force refresh if needed
      const refreshFlag = checkRefreshFlag(contractAddress);
      if (refreshFlag.isStale) {
        console.log('Detected stale refresh flag, performing force refresh');
        forceRefresh = true;
        setIsRecoveryRefresh(true);
        setRefreshFlag(contractAddress, false); // Clear stale flag
      } else if (refreshFlag.isRefreshing && !forceRefresh) {
        console.log('Detected interrupted refresh, performing force refresh');
        forceRefresh = true;
        setIsRecoveryRefresh(true);
      }

      // Set refresh flag at the start of refresh (after mutex check)
      setRefreshFlag(contractAddress, true);

      const contract = getGovernanceContract(provider, contractAddress);
      
      // Load basic governance stats
      const [proposalCount, quorumNumerator, quorumDenominator] = await Promise.all([
        contract.proposalCount(),
        contract.quorumNumerator(),
        contract.quorumDenominator()
      ]);

      const quorum = Number(quorumNumerator) / Number(quorumDenominator) * 100;

      // Load events with caching
      let events: any[] = [];
      let lastFetchedBlock = 0;
      let cached = { events: [], lastBlock: 0, timestamp: 0, cacheExists: false };
      
      if (!forceRefresh) {
        // Try to load from cache first
        cached = loadCachedEvents(contractAddress);
        // Cached events are already in serialized format
        events = cached.events;
        lastFetchedBlock = cached.lastBlock;
        console.log(`Loaded ${events.length} events from cache, last block: ${lastFetchedBlock}, cache exists: ${cached.cacheExists}`);
      }

      try {
        const currentBlock = await provider.getBlockNumber();
        
        if (forceRefresh || events.length === 0 || lastFetchedBlock < currentBlock) {
          let fromBlock: number;
          
          if (forceRefresh) {
            // For force refresh, start from contract deployment block
            fromBlock = await getContractDeploymentBlock(contract, provider, contractAddress);
            console.log(`Force refresh: Fetching events from deployment block ${fromBlock} to ${currentBlock}`);
          } else if (!cached.cacheExists) {
            // If no cache exists, start from contract deployment block
            fromBlock = await getContractDeploymentBlock(contract, provider, contractAddress);
            console.log(`No cache found: Fetching events from deployment block ${fromBlock} to ${currentBlock}`);
          } else {
            // For incremental refresh, start from last cached block + 1
            fromBlock = lastFetchedBlock + 1;
            console.log(`Incremental refresh: Fetching events from block ${fromBlock} to ${currentBlock}`);
          }
          
          // Fetch new events from the determined start block to current block
          const newEvents = await fetchEventsInChunks(
            contract, 
            currentBlock, 
            provider, 
            contractAddress, 
            Number(proposalCount),
            fromBlock
          );
          
          // Convert new events to serialized format for consistency
          const serializedNewEvents = newEvents.map(event => ({
            proposalId: event.args.proposalId.toString(),
            proposer: event.args.proposer,
            description: event.args.description,
            voteStart: event.args.voteStart.toString(),
            voteEnd: event.args.voteEnd.toString(),
            signatures: event.args.signatures,
            blockNumber: event.blockNumber,
            transactionHash: event.transactionHash
          }));
          
          if (forceRefresh) {
            // Force refresh: replace all events with fresh data
            events = serializedNewEvents;
          } else {
            // Incremental refresh: merge with cached events (remove duplicates by proposalId)
            const existingProposalIds = new Set(events.map(e => e.proposalId));
            const uniqueNewEvents = serializedNewEvents.filter(e => !existingProposalIds.has(e.proposalId));
            events = [...events, ...uniqueNewEvents];
          }
          lastFetchedBlock = currentBlock;
          
          // Save updated cache (events are already in serialized format)
          saveCachedEvents(contractAddress, events, lastFetchedBlock);
          console.log(`Cached ${events.length} total events, last block: ${lastFetchedBlock}`);
        }
        
        if (events.length > 0) {
          setEventDataAvailable(true);
        } else {
          console.warn('No events found');
          setEventDataAvailable(false);
        }
      } catch (eventError) {
        console.warn('Event fetching failed:', eventError);
        setEventDataAvailable(false);
        // Continue without events - we'll just have empty descriptions/proposers
      }

      const proposalPromises = [];
      const totalProposals = Number(proposalCount);
      for (let i = 0; i < totalProposals; i++) {
        proposalPromises.push(loadProposalDetails(contract, i, events));
      }

      const loadedProposals = await Promise.all(proposalPromises);
      const validProposals = loadedProposals.filter(p => p !== null) as Proposal[];

      setProposals(validProposals);
      setGovernanceStats({
        totalProposals: totalProposals,
        activeProposals: validProposals.filter(p => p.state === ProposalState.Active).length,
        totalVotes: 0, // Would need to calculate from all proposals
        quorum: quorum
      });

      // Clear refresh flag on successful completion
      setRefreshFlag(contractAddress, false);
      setIsRecoveryRefresh(false);
      console.log('Refresh completed successfully, cleared refresh flag');

    } catch (err: any) {
      console.error('Error loading governance data:', err);
      
      // Clear refresh flag on error as well
      setRefreshFlag(contractAddress, false);
      setIsRecoveryRefresh(false);
      console.log('Refresh failed, cleared refresh flag');
      
      // Provide more specific error messages based on the error type
      if (err.code === -32002 || err.message?.includes('RPC endpoint not found')) {
        setError('Network connection failed. Please check your internet connection and try again.');
      } else if (err.code === 'UNSUPPORTED_OPERATION') {
        setError('This contract address is not a valid governance contract.');
      } else if (err.message?.includes('execution reverted')) {
        setError('Contract call failed. The contract may not be deployed or accessible.');
      } else {
        setError('Failed to load governance data. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
      isLoadingDataRef.current = false; // Clear ref mutex
    }
  }, [provider, contractAddress, loadCachedEvents, saveCachedEvents, checkRefreshFlag, setRefreshFlag]);

  // Load individual proposal details
  const loadProposalDetails = async (contract: any, proposalIdx: number, events: any[]): Promise<Proposal | null> => {
    try {
      // First get the proposal details using the index
      const proposalDetails = await contract.proposalDetailsAt(proposalIdx);
      const [actualProposalId, targets, values, calldatas] = proposalDetails;
      
      // Now use the actual proposal ID for state and votes
      const [state, votes, proposer] = await Promise.all([
        contract.state(actualProposalId),
        contract.proposalVotes(actualProposalId),
        contract.proposalProposer(actualProposalId).catch(() => '') // Get proposer directly from contract
      ]);

      // Try to find the event from the pre-fetched events first
      // Events can be either ethers.js events (with args) or serialized events (with direct properties)
      let proposalEvent = events.find(event => {
        if (event && event.args && event.args.proposalId) {
          // Fresh ethers.js event
          return event.args.proposalId.toString() === actualProposalId.toString();
        } else if (event && event.proposalId) {
          // Serialized cached event
          return event.proposalId === actualProposalId.toString();
        }
        return false;
      });
      
      if (proposalEvent) {
        // Event data available - use it for complete information
      } else {
        // No event data - we'll use contract calls for basic info
      }
      
      // If not found in pre-fetched events, we can't easily fetch individual events
      // since proposalId is not indexed. We'll just continue without the event data.
      // In a production app, you might want to use a subgraph or indexer for this.
      
      let description = '';
      let voteStart = '0';
      let voteEnd = '0';
      let signatures: string[] = [];

      if (proposalEvent) {
        // Use event data if available - handle both ethers.js and serialized formats
        if (proposalEvent.args) {
          // Fresh ethers.js event
          description = proposalEvent.args.description;
          voteStart = proposalEvent.args.voteStart.toString();
          voteEnd = proposalEvent.args.voteEnd.toString();
          signatures = proposalEvent.args.signatures;
        } else {
          // Serialized cached event
          description = proposalEvent.description;
          voteStart = proposalEvent.voteStart;
          voteEnd = proposalEvent.voteEnd;
          signatures = proposalEvent.signatures;
        }
      } else {
        // Use proposer from contract call (we already have this)
        // Description and other details will be empty but proposer is available
      }
      
      return {
        id: actualProposalId.toString(),
        proposer,
        targets,
        values: values.map((v: any) => v.toString()),
        signatures,
        calldatas,
        voteStart,
        voteEnd,
        description,
        state: Number(state) as ProposalState,
        forVotes: votes[1].toString(),
        againstVotes: votes[0].toString(),
        abstainVotes: votes[2].toString()
      };
    } catch (err: any) {
      console.error(`Error loading proposal ${proposalIdx}:`, err);
      
      // If it's a network error, we might want to retry or show a partial proposal
      if (err.code === -32002 || err.message?.includes('RPC endpoint not found')) {
        console.warn(`Skipping proposal ${proposalIdx} due to network error`);
        return null;
      }
      
      return null;
    }
  };

  // Load data on mount and when dependencies change
  useEffect(() => {
    if (isConnected && isAddressSet && provider && contractAddress && !hasLoadedData.current && !isLoadingDataRef.current) {
      hasLoadedData.current = true;
      loadGovernanceData();
    }
  }, [isConnected, isAddressSet, provider, contractAddress, loadGovernanceData]);

  // Reset hasLoadedData when contract address changes
  useEffect(() => {
    hasLoadedData.current = false;
  }, [contractAddress]);

  // Handle proposal creation
  const handleProposalCreated = useCallback(() => {
    setShowCreateForm(false);
    hasLoadedData.current = false; // Allow refresh after proposal creation
    loadGovernanceData(false);
  }, [loadGovernanceData]);

  // Handle voting
  const handleVote = useCallback(async (proposalId: string, support: VoteType, reason?: string) => {
    if (!signer || !contractAddress) return;

    try {
      setLoading(true);
      setError('');

      const contract = getGovernanceSignerContract(signer, contractAddress);
      
      let tx;
      if (reason) {
        tx = await contract.castVoteWithReason(proposalId, support, reason);
      } else {
        tx = await contract.castVote(proposalId, support);
      }

      await tx.wait();
      
      // Refresh the proposal data
      hasLoadedData.current = false; // Allow refresh after voting
      loadGovernanceData();
      setSelectedProposal(null);
      
    } catch (err) {
      console.error('Error voting:', err);
      setError('Failed to cast vote. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [signer, contractAddress, loadGovernanceData]);

  if (!currentNetwork) {
    return (
      <div className="governance-page">
        <Header subtitle="Governance" />
        <div className="container">
          <div className="no-network">
            <h3>No Network Selected</h3>
            <p>Please select a network to access governance features.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="governance-page">
        <Header subtitle="Governance" />
        <div className="container">
          <div className="connect-section">
            <h2>Connect Your Wallet</h2>
            <p>Please connect your wallet to access governance features.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="governance-page">
      <Header subtitle="Governance" />
      <div className="container">
        <div className="main-content">
          <PolicyDescription 
            currentNetwork={currentNetwork}
            contractAddress={contractAddress}
          />
          <GovernanceHeader 
            stats={governanceStats}
            onCreateProposal={() => setShowCreateForm(true)}
            onRefresh={() => {
              hasLoadedData.current = false;
              loadGovernanceData(false);
            }}
            onForceRefresh={() => {
              hasLoadedData.current = false;
              loadGovernanceData(true);
            }}
            loading={loading}
          />

          {loading && (
            <div className="smart-refresh-notification">
              <span className="refresh-icon">🔄</span>
              {isRecoveryRefresh ? (
                <>
                  Recovering from interrupted refresh. Please wait a moment while loading proposals, this may take several minutes...
                </>
              ) : (
                <>
                  Please wait a moment while loading proposals, this may take several minutes at the first connection...
                </>
              )}
            </div>
          )}

          {error && (
            <div className="error">
              <div className="error-message">
                {error}
              </div>
              <div className="error-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    hasLoadedData.current = false;
                    loadGovernanceData(false);
                  }}
                  disabled={loading}
                >
                  Retry
                </button>
                {error.includes('Network connection failed') && (
                  <div className="error-tips">
                    <p><strong>Tips to resolve network issues:</strong></p>
                    <ul>
                      <li>Check your internet connection</li>
                      <li>Try switching to a different network</li>
                      <li>Refresh the page and try again</li>
                      <li>Make sure MetaMask is connected to the correct network</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {!eventDataAvailable && !error && (
            <div className="warning">
              <div className="warning-message">
                ⚠️ Some proposal details (descriptions, proposers) are not available due to network limitations. 
                Core governance functionality is still working.
              </div>
            </div>
          )}

          <ProposalList 
            proposals={proposals}
            onSelectProposal={setSelectedProposal}
            loading={loading}
          />

          {showCreateForm && (
            <CreateProposalForm
              onClose={() => setShowCreateForm(false)}
              onProposalCreated={handleProposalCreated}
            />
          )}

          {selectedProposal && (
            <VotingPanel
              proposal={selectedProposal}
              onVote={handleVote}
              onClose={() => setSelectedProposal(null)}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GovernancePage;
